import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { useQueryClient } from '@tanstack/react-query';

import Blade from '@/components/ui/Blade/Blade';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs/Tabs';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import DownloadFileButton from '@/components/ui/DownloadFileButton/DownloadFileButton';
import { useGetAuthApi, usePostAuthApi, usePutAuthApi } from '@/hooks/UseApiCalls';
import {
  FundManagementDetailsDtoIn,
  FundManagementSupported,
  calculateAvailableBalance,
} from '@/models/in/FundManagementDetailsDtoIn';
import { FundStatus } from '@/models/common/FundStatus';
import { UpdateFundStatusDtoOut } from '@/models/out/UpdateFundStatusDtoOut';
import { InactiveFundCandidateDtoIn } from '@/models/in/InactiveFundCandidateDtoIn';
import { toLocalPhone } from '@/helpers/phoneHelper';
import editIcon from '@/assets/icons/edit.svg';
import bankIcon from '@/assets/icons/bank.svg';
import detailsIcon from '@/assets/icons/id2.svg';
import fundSettingsIcon from '@/assets/icons/fundSettings.svg';
import NoteDialog from '../NoteDialog';
import EditFundDetailsDialog from '../EditFundDetailsDialog';
import EditFundFeaturesDialog from '../EditFundFeaturesDialog';
import EditCoordinatorsDialog from '../EditCoordinatorsDialog';
import EditSupportedsDialog from '../EditSupportedsDialog';
import ReactivateFundDialog from '../ReactivateFundDialog';
import CampaignsTab from './CampaignsTab';
import DevicesTab from './DevicesTab';
import { DEVICE_RENTALS_FEATURE_KEY } from '@/helpers/paymentDevices';
import MailDocumentsSection from './MailDocumentsSection';
import useFeature from '@/contexts/FeatureFlags/useFeature';
import { FUND_CAMPAIGNS_FEATURE_KEY } from '../fundCampaigns';
import styles from './FundBlade.module.css';
import OfficePeopleList from '@/pages/FundSettings/people/OfficePeopleList';

const extractServerError = (error: Error): string | null => {
  const data = (error as AxiosError).response?.data;
  if (!data) return null;
  if (typeof data === 'string') return data;
  if (typeof data === 'object' && 'message' in data) return String((data as { message: unknown }).message);
  return null;
};

type EditDialog = 'details' | 'features' | 'coordinators' | 'supporteds' | null;

export type FundBladeTab = 'details' | 'coordinators' | 'supporteds' | 'campaigns' | 'devices';

interface FundBladeProps {
  fundId: number;
  fundName: string;
  handleXclick: () => void;
  /** Which tab opens first - the table's coordinators/supporteds buttons land straight on theirs. */
  initialTab?: FundBladeTab;
  /** HAT-1301: the fund's row in the inactivity candidates list, when the board has fetched it. */
  inactivity?: InactiveFundCandidateDtoIn | null;
}

const FundBlade = ({ fundId, fundName, handleXclick, initialTab = 'details', inactivity = null }: FundBladeProps) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const fundUrl = `funds/${fundId}/management`;
  const { data: fund, isLoading } = useGetAuthApi<FundManagementDetailsDtoIn>(fundUrl);

  // The campaigns tab is withheld until the flag names this user (HAT-1121). Its endpoints carry
  // the same flag, so nothing here is merely hidden.
  const hasCampaigns = useFeature(FUND_CAMPAIGNS_FEATURE_KEY);
  // A caller can ask for a tab that the flag has taken away; opening on a tab that does not exist
  // would leave the blade blank, so fall back to the details tab.
  // The terminals tab (HAT-1434) follows the device-rentals flag, evaluated for this user like the page itself.
  const hasDevices = useFeature(DEVICE_RENTALS_FEATURE_KEY);
  const openingTab = (initialTab === 'campaigns' && !hasCampaigns) || (initialTab === 'devices' && !hasDevices) ? 'details' : initialTab;

  const [openDialog, setOpenDialog] = useState<EditDialog>(null);
  const [pendingStatus, setPendingStatus] = useState<FundStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [isNedarimConfirmOpen, setIsNedarimConfirmOpen] = useState(false);
  const [nedarimResult, setNedarimResult] = useState<'success' | 'error' | null>(null);
  // HAT-1301: freezing (destroys the documents) and reactivating (needs new ones)
  const [isFreezeNoteOpen, setIsFreezeNoteOpen] = useState(false);
  const [isReactivateOpen, setIsReactivateOpen] = useState(false);
  const [isDestroyNoteOpen, setIsDestroyNoteOpen] = useState(false);
  // HAT-1301: the office's decision on documents the coordinator sent
  const [reviewing, setReviewing] = useState<'approve' | 'reject' | null>(null);

  const invalidateFundLists = () => {
    queryClient.invalidateQueries({ queryKey: [fundUrl] });
    queryClient.invalidateQueries({ queryKey: [`funds/management?status=${FundStatus.Active}`] });
    queryClient.invalidateQueries({ queryKey: [`funds/management?status=${FundStatus.Closed}`] });
    queryClient.invalidateQueries({ queryKey: [`funds/management?status=${FundStatus.Frozen}`] });
    queryClient.invalidateQueries({ queryKey: ['funds/management/inactive-candidates'] });
    queryClient.invalidateQueries({ queryKey: ['funds/management/reactivation-requests'] });
  };

  const reviewMutationOptions = {
    onSuccess: () => {
      setReviewing(null);
      setStatusError(null);
      invalidateFundLists();
    },
    onError: (error: Error) => {
      setReviewing(null);
      setStatusError(extractServerError(error) ?? t('fundsManagement.saveError'));
    },
  };
  const approveRequest = usePostAuthApi<void, { note: string }>(
    `funds/${fundId}/management/reactivation-request/approve`,
    reviewMutationOptions
  );
  const rejectRequest = usePostAuthApi<void, { note: string }>(
    `funds/${fundId}/management/reactivation-request/reject`,
    reviewMutationOptions
  );

  const freezeFund = usePostAuthApi<void, { note: string }>(
    `funds/${fundId}/management/freeze`,
    {
      onSuccess: () => {
        setIsFreezeNoteOpen(false);
        setStatusError(null);
        invalidateFundLists();
      },
      onError: (error) => {
        setIsFreezeNoteOpen(false);
        setStatusError(extractServerError(error) ?? t('fundsManagement.saveError'));
      },
    }
  );

  // HAT-1301: the automatic run freezes but never destroys, so a frozen fund can still be holding
  // its documents. Destroying them is the office's own confirmed act, on the fund it is looking at.
  const destroyDocuments = usePostAuthApi<{ destroyed: number }, { note: string }>(
    `funds/${fundId}/management/destroy-documents`,
    {
      onSuccess: () => {
        setIsDestroyNoteOpen(false);
        setStatusError(null);
        invalidateFundLists();
      },
      onError: (error) => {
        setIsDestroyNoteOpen(false);
        setStatusError(extractServerError(error) ?? t('fundsManagement.saveError'));
      },
    }
  );

  const updateStatus = usePutAuthApi<void, object, UpdateFundStatusDtoOut>(
    `funds/${fundId}/management/status`,
    {
      onSuccess: () => {
        setPendingStatus(null);
        setStatusError(null);
        invalidateFundLists();
      },
      onError: (error) => setStatusError(extractServerError(error) ?? t('fundsManagement.saveError')),
    }
  );

  const sendNedarimRequest = usePostAuthApi<void, object>(
    `funds/${fundId}/management/nedarim-interface-request`,
    {
      onSuccess: () => {
        setIsNedarimConfirmOpen(false);
        setNedarimResult('success');
      },
      onError: () => {
        setIsNedarimConfirmOpen(false);
        setNedarimResult('error');
      },
    }
  );

  const formatAmount = (value: number, currency?: string | null) =>
    value.toLocaleString(i18n.language, { style: 'currency', currency: currency || 'ILS' });

  const formatDate = (value?: string | null) => {
    if (!value) return '';
    if (value.slice(0, 4) === '2100') return t('fundsManagement.noEndDate');
    return new Date(value).toLocaleDateString('he-IL');
  };

  const formatSupportedAddress = (supported: FundManagementSupported) => {
    const street = [supported.street, supported.houseNumber].filter(Boolean).join(' ');
    return [street, supported.city].filter(Boolean).join(', ');
  };

  const metaParts = (parts: { text: string; ltr?: boolean }[]) => {
    const visible = parts.filter((part) => part.text);
    return (
      <span className={styles.personMeta}>
        {visible.map((part, index) => (
          <Fragment key={index}>
            {index > 0 && <span className={styles.metaDot}>•</span>}
            <span dir={part.ltr ? 'ltr' : undefined}>{part.text}</span>
          </Fragment>
        ))}
      </span>
    );
  };

  const editButton = (dialog: Exclude<EditDialog, null>) => (
    <button type="button" className={styles.editIconBtn} onClick={() => setOpenDialog(dialog)}>
      <img src={editIcon} alt="" />
      {t('fundsManagement.editButton')}
    </button>
  );

  const isActive = fund?.status === FundStatus.Active;
  const isFrozen = fund?.status === FundStatus.Frozen;
  const balances = fund?.fundBalances;
  const features = fund?.features;
  const coordinators = fund?.coordinators ?? [];
  const supporteds = fund?.supporteds ?? [];
  const documents = fund?.documents ?? [];

  const headerTop = (
    <div className="flex items-center gap-3">
      <span className="body-lr-semibold text-(--font-body-4)">
        {fundId} | {fund?.name ?? fundName}
      </span>
      {fund && (
        <span className={styles.headerBadge}>{t(`fundsManagement.status.${fund.status}`)}</span>
      )}
    </div>
  );

  // Balances stay in the brand color even when negative, matching the sidebar balance panel.
  const stat = (label: string, value: number) => (
    <div className={styles.stat}>
      <span className={styles.statLabel}>{label}</span>
      <span className={styles.statValue}>{formatAmount(value, balances?.currency)}</span>
    </div>
  );

  const featureRow = (name: string, isOn: boolean, details: string | null) => (
    <div className={styles.featureRow}>
      <div className={styles.featureHeader}>
        <span className={styles.featureName}>{name}</span>
        <span className={`${styles.featureBadge} ${isOn ? styles.featureBadgeOn : styles.featureBadgeOff}`}>
          {isOn ? t('fundsManagement.featureActive') : t('fundsManagement.featureInactive')}
        </span>
      </div>
      {isOn && details && <span className={styles.featureDetails}>{details}</span>}
    </div>
  );

  return (
    <Blade handleXclick={handleXclick} headerTop={headerTop} headerBottom={<></>} width="520px">
      <Tabs defaultValue={openingTab} style={{ direction: 'inherit' }}>
        <TabsList>
          <TabsTrigger value="details">{t('fundsManagement.detailsSection')}</TabsTrigger>
          <TabsTrigger value="coordinators">{t('fundsManagement.coordinators')}</TabsTrigger>
          <TabsTrigger value="supporteds">{t('fundsManagement.supporteds')}</TabsTrigger>
          {hasCampaigns && (
            <TabsTrigger value="campaigns">{t('fundsManagement.campaigns.tab')}</TabsTrigger>
          )}
          {hasDevices && (
            <TabsTrigger value="devices">{t('paymentDevices.fundTab.tab')}</TabsTrigger>
          )}
        </TabsList>

        {/* ---------- Fund details tab ---------- */}
        <TabsContent value="details" className={`${styles.content} custom-scrollbar`}>
          {isLoading || !fund ? (
            <p className={styles.emptyText}>{t('loading')}...</p>
          ) : (
            <>
              {/* Details */}
              <div>
                <div className={styles.sectionHeader}>
                  <h2 className={styles.sectionTitle}>
                    <img src={detailsIcon} alt="" />
                    {t('fundsManagement.detailsSection')}
                  </h2>
                  {editButton('details')}
                </div>
                <div className={`${styles.detailsList} mt-3`}>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('fundsManagement.classification')}</span>
                    <span className={styles.detailValue}>
                      {t(`fundsManagement.classificationNames.${fund.classification}`)}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('fundsManagement.internalClassification')}</span>
                    <span className={styles.detailValue}>{fund.internalClassification || '—'}</span>
                  </div>
                  {isFrozen && (
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>{t('fundsManagement.frozenAt')}</span>
                      <span className={styles.detailValue}>{fund.frozenAt ? formatDate(fund.frozenAt) : '—'}</span>
                    </div>
                  )}
                  {/* HAT-1301: the coordinator sent identity documents to bring this fund back. The fund
                      stays frozen until someone here has opened them and said yes - or no, with a reason. */}
                  {isFrozen && fund.pendingReactivation && (
                    <div className={styles.pendingRequest}>
                      <strong>{t('fundsManagement.pendingRequest.title')}</strong>
                      <span className={styles.detailLabel}>
                        {t('fundsManagement.pendingRequest.sentBy', {
                          email: fund.pendingReactivation.submittedByEmail,
                          date: formatDate(fund.pendingReactivation.submittedAt),
                        })}
                      </span>
                      <span className={styles.documentLinks}>
                        {fund.pendingReactivation.documents.map((document) => (
                          <span key={document.uri} className="flex items-center gap-1">
                            <a
                              href={document.uri}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="body-md-link text-(--font-link)"
                            >
                              {document.name}
                            </a>
                            <DownloadFileButton uri={document.uri} fileName={document.name} />
                          </span>
                        ))}
                      </span>
                      <div className={styles.actionsRow}>
                        <button
                          type="button"
                          className={styles.pillPrimary}
                          disabled={approveRequest.isPending || rejectRequest.isPending}
                          onClick={() => {
                            setStatusError(null);
                            setReviewing('approve');
                          }}
                        >
                          {t('fundsManagement.pendingRequest.approve')}
                        </button>
                        <button
                          type="button"
                          className={styles.pillOutline}
                          disabled={approveRequest.isPending || rejectRequest.isPending}
                          onClick={() => {
                            setStatusError(null);
                            setReviewing('reject');
                          }}
                        >
                          {t('fundsManagement.pendingRequest.reject')}
                        </button>
                      </div>
                    </div>
                  )}
                  {inactivity && isActive && (
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>{t('fundsManagement.inactivity.title')}</span>
                      <span className={styles.detailValue}>
                        {inactivity.lastMovementDate
                          ? t('fundsManagement.inactivity.lastMovement', { date: formatDate(inactivity.lastMovementDate) })
                          : t('fundsManagement.inactivity.neverMoved', { date: formatDate(inactivity.registeredAt) })}
                        {' · '}
                        {t('fundsManagement.inactivity.documents', { count: inactivity.documentsCount })}
                      </span>
                    </div>
                  )}
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('fundsManagement.documents')}</span>
                    <span className={styles.detailValue}>
                      {documents.length === 0 ? t('fundsManagement.noDocuments') : (
                        <span className={styles.documentLinks}>
                          {documents.map((document) => (
                            <span key={document.uri} className="flex items-center gap-1">
                              <a
                                href={document.uri}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="body-md-link text-(--font-link)"
                              >
                                {document.name}
                              </a>
                              <DownloadFileButton uri={document.uri} fileName={document.name} />
                            </span>
                          ))}
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              </div>

              <div className={styles.line} />

              {/* HAT-1426: the fund's documents still sitting in the office mail */}
              {isFrozen && (
                <>
                  <MailDocumentsSection fundId={fundId} />
                  <div className={styles.line} />
                </>
              )}

              {/* Balances */}
              <div>
                <div className={styles.sectionHeader}>
                  <h2 className={styles.sectionTitle}>
                    <img src={bankIcon} alt="" />
                    {t('fundsManagement.balancesSection')}
                  </h2>
                </div>
                {balances ? (
                  <div className={`${styles.balancesList} mt-3`}>
                    {stat(t('fundsManagement.balance'), balances.balancesByKind.total)}
                    {stat(t('fundsManagement.balanceForWithdrawal'), calculateAvailableBalance(balances))}
                    {stat(t('fundsManagement.pendingExecutionSum'), balances.supportRequestsBalances.pendingExecutionSum)}
                    {stat(t('fundsManagement.overdraftAmount'), balances.overdraftAmount)}
                  </div>
                ) : (
                  <p className={`${styles.emptyText} mt-3`}>{t('fundsManagement.noBalances')}</p>
                )}
              </div>

              <div className={styles.line} />

              {/* Special settings */}
              <div>
                <div className={styles.sectionHeader}>
                  <h2 className={styles.sectionTitle}>
                    <img src={fundSettingsIcon} alt="" />
                    {t('fundsManagement.featuresSection')}
                  </h2>
                  {editButton('features')}
                </div>
                <div className={`${styles.featureList} mt-3`}>
                  {featureRow(
                    t('fundsManagement.overdraft'),
                    Boolean(features?.overdraft),
                    features?.overdraft
                      ? `${formatAmount(features.overdraft.amount)} · ${formatDate(features.overdraft.startDate)} - ${formatDate(features.overdraft.endDate)}`
                      : null,
                  )}
                  {featureRow(
                    t('fundsManagement.fundFreeze'),
                    Boolean(features?.fundFreeze),
                    features?.fundFreeze
                      ? `${formatDate(features.fundFreeze.startDate)} - ${formatDate(features.fundFreeze.endDate)}`
                      : null,
                  )}
                  {featureRow(
                    t('fundsManagement.supportRequestsFreeze'),
                    Boolean(features?.supportRequestsFreeze),
                    features?.supportRequestsFreeze
                      ? `${formatDate(features.supportRequestsFreeze.startDate)} - ${formatDate(features.supportRequestsFreeze.endDate)}`
                      : null,
                  )}
                  {features?.reason && (
                    <div className={styles.featureRow}>
                      <span className={styles.featureName}>{t('fundsManagement.featuresReason')}</span>
                      <span className={styles.featureDetails}>{features.reason}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.line} />

              {/* Actions */}
              <div>
                <h2 className={styles.sectionTitle}>{t('fundsManagement.actionsSection')}</h2>
                <div className="mt-3">
                  <div className={styles.actionsRow}>
                    <button
                      type="button"
                      className={styles.pillOutline}
                      disabled={sendNedarimRequest.isPending}
                      onClick={() => {
                        setNedarimResult(null);
                        setIsNedarimConfirmOpen(true);
                      }}
                    >
                      {sendNedarimRequest.isPending ? t('loading') : t('fundsManagement.nedarimInterfaceButton')}
                    </button>

                    {/* HAT-1301: a frozen fund leaves that state only through reactivation, which
                        demands the coordinator's new ID documents - so no plain status button here. */}
                    {isFrozen ? (
                      <button
                        type="button"
                        className={styles.pillPrimary}
                        onClick={() => setIsReactivateOpen(true)}
                      >
                        {t('fundsManagement.reactivateFrozenFund')}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className={styles.pillPrimary}
                        disabled={updateStatus.isPending}
                        onClick={() => setPendingStatus(isActive ? FundStatus.Closed : FundStatus.Active)}
                      >
                        {isActive ? t('fundsManagement.closeFund') : t('fundsManagement.reopenFund')}
                      </button>
                    )}

                    {isActive && (
                      <button
                        type="button"
                        className={styles.pillOutline}
                        disabled={freezeFund.isPending}
                        onClick={() => {
                          setStatusError(null);
                          setIsFreezeNoteOpen(true);
                        }}
                      >
                        {freezeFund.isPending ? t('loading') : t('fundsManagement.freezeFund')}
                      </button>
                    )}

                    {/* A fund frozen by the automatic run keeps its documents until someone here
                        says otherwise. This is that "otherwise", and it cannot be undone. */}
                    {isFrozen && documents.length > 0 && (
                      <button
                        type="button"
                        className={styles.pillOutline}
                        disabled={destroyDocuments.isPending}
                        onClick={() => {
                          setStatusError(null);
                          setIsDestroyNoteOpen(true);
                        }}
                      >
                        {destroyDocuments.isPending
                          ? t('loading')
                          : t('fundsManagement.destroyDocuments', { count: documents.length })}
                      </button>
                    )}
                  </div>

                  {nedarimResult === 'success' && (
                    <p className={`${styles.actionResult} mt-2`}>{t('fundsManagement.nedarimInterfaceSuccess')}</p>
                  )}
                  {nedarimResult === 'error' && (
                    <p className={`${styles.actionError} mt-2`}>{t('fundsManagement.nedarimInterfaceError')}</p>
                  )}
                  {statusError && <p className={`${styles.actionError} mt-2`}>{statusError}</p>}
                </div>
              </div>
            </>
          )}
        </TabsContent>

        {/* ---------- Coordinators tab ---------- */}
        <TabsContent value="coordinators" className={`${styles.content} custom-scrollbar`}>
          {isLoading || !fund ? (
            <p className={styles.emptyText}>{t('loading')}...</p>
          ) : (
            /* Where «רכזים והרשאות» is on for the fund, the people list: the office adds coordinators
               there, with an Israeli ID, and takes them out (Druk, 22.10.2026) - a lead coordinator
               too, since 3.11.2026. Elsewhere the plain list and «עריכה», as before. */
            <OfficePeopleList
              fundId={fund.id}
              onChanged={invalidateFundLists}
              fallback={
                <div>
                  <div className={styles.tabToolbar}>{editButton('coordinators')}</div>
                  {coordinators.length === 0 ? (
                    <p className={styles.emptyText}>{t('fundsManagement.noCoordinators')}</p>
                  ) : (
                    <div className={styles.personList}>
                      {coordinators.map((coordinator, index) => (
                        <div className={styles.personRow} key={`${coordinator.email}-${index}`}>
                          <span className={styles.personName}>{coordinator.firstName} {coordinator.lastName}</span>
                          {metaParts([
                            { text: toLocalPhone(coordinator.phone), ltr: true },
                            { text: coordinator.email ?? '', ltr: true },
                          ])}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              }
            />
          )}
        </TabsContent>

        {/* ---------- Supporteds tab ---------- */}
        <TabsContent value="supporteds" className={`${styles.content} custom-scrollbar`}>
          {isLoading || !fund ? (
            <p className={styles.emptyText}>{t('loading')}...</p>
          ) : (
            <div>
              <div className={styles.tabToolbar}>{editButton('supporteds')}</div>
              {supporteds.length === 0 ? (
                <p className={styles.emptyText}>{t('fundsManagement.noSupporteds')}</p>
              ) : (
                <div className={styles.personList}>
                  {supporteds.map((supported, index) => (
                    <div className={styles.personRow} key={`${supported.israeliId}-${index}`}>
                      <span className={styles.personName}>{supported.firstName} {supported.lastName}</span>
                      {metaParts([
                        { text: supported.israeliId ? `${t('fundsManagement.israeliId')} ${supported.israeliId}` : '' },
                        { text: formatSupportedAddress(supported) },
                        { text: toLocalPhone(supported.phone), ltr: true },
                        { text: supported.email ?? '', ltr: true },
                      ])}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </TabsContent>
        {/* ---------- Campaigns tab (HAT-1121), behind the fund-campaigns flag ---------- */}
        {hasCampaigns && (
          <TabsContent value="campaigns" className={`${styles.content} custom-scrollbar`}>
            <CampaignsTab fundId={fundId} />
          </TabsContent>
        )}
        {/* ---------- Payment terminals tab (HAT-1434), behind the device-rentals flag ---------- */}
        {hasDevices && (
          <TabsContent value="devices" className={`${styles.content} custom-scrollbar`}>
            <DevicesTab fundId={fundId} fundName={fund?.name ?? fundName} />
          </TabsContent>
        )}
      </Tabs>

      {/* Edit dialogs */}
      {fund && (
        <>
          <EditFundDetailsDialog open={openDialog === 'details'} onClose={() => setOpenDialog(null)} fund={fund} />
          <EditFundFeaturesDialog open={openDialog === 'features'} onClose={() => setOpenDialog(null)} fund={fund} />
          <EditCoordinatorsDialog open={openDialog === 'coordinators'} onClose={() => setOpenDialog(null)} fund={fund} />
          <EditSupportedsDialog open={openDialog === 'supporteds'} onClose={() => setOpenDialog(null)} fund={fund} />
        </>
      )}

      {/* Nedarim personal-interface confirmation */}
      <ConfirmationDialog
        open={isNedarimConfirmOpen}
        onOpenChange={setIsNedarimConfirmOpen}
        title={t('fundsManagement.nedarimInterfaceConfirmTitle')}
        message={t('fundsManagement.nedarimInterfaceConfirmMessage', { id: fundId, name: fund?.name ?? fundName })}
        confirmText="fundsManagement.nedarimInterfaceConfirmSend"
        cancelText="fundsManagement.cancel"
        isLoading={sendNedarimRequest.isPending}
        onConfirm={() => sendNedarimRequest.mutate({})}
      />

      {/* HAT-1301: freeze confirmation - the note is mandatory, and the message says what is destroyed */}
      <NoteDialog
        open={isFreezeNoteOpen}
        onClose={() => setIsFreezeNoteOpen(false)}
        title={t('fundsManagement.freezeFundNoteTitle')}
        message={t('fundsManagement.freezeFundNoteMessage', { id: fundId, count: documents.length })}
        confirmText={t('fundsManagement.freezeFundConfirm')}
        isLoading={freezeFund.isPending}
        onConfirm={(note) => freezeFund.mutate({ note })}
      />

      {/* HAT-1301: destroying the documents of an already-frozen fund */}
      <NoteDialog
        open={isDestroyNoteOpen}
        onClose={() => setIsDestroyNoteOpen(false)}
        title={t('fundsManagement.destroyDocumentsTitle')}
        message={t('fundsManagement.destroyDocumentsMessage', { id: fundId, count: documents.length })}
        confirmText={t('fundsManagement.destroyDocumentsConfirm')}
        isLoading={destroyDocuments.isPending}
        onConfirm={(note) => destroyDocuments.mutate({ note })}
      />

      {/* HAT-1301: the office's decision on the documents the coordinator sent. Declining shows
          the note to the coordinator, so the message says so before it is written. */}
      <NoteDialog
        open={reviewing !== null}
        onClose={() => setReviewing(null)}
        title={reviewing === 'approve' ? t('fundsManagement.pendingRequest.approveTitle') : t('fundsManagement.pendingRequest.rejectTitle')}
        message={
          reviewing === 'approve'
            ? t('fundsManagement.pendingRequest.approveMessage', { id: fundId })
            : t('fundsManagement.pendingRequest.rejectMessage', { id: fundId })
        }
        confirmText={reviewing === 'approve' ? t('fundsManagement.pendingRequest.approveConfirm') : t('fundsManagement.pendingRequest.rejectConfirm')}
        isLoading={approveRequest.isPending || rejectRequest.isPending}
        onConfirm={(note) => (reviewing === 'approve' ? approveRequest.mutate({ note }) : rejectRequest.mutate({ note }))}
      />

      {/* HAT-1301: reactivation with the coordinator's new ID documents */}
      <ReactivateFundDialog
        open={isReactivateOpen}
        onClose={() => setIsReactivateOpen(false)}
        fundId={fundId}
        fundName={fund?.name ?? fundName}
        onSaved={invalidateFundLists}
      />

      {/* Status change note dialog */}
      <NoteDialog
        open={pendingStatus !== null}
        onClose={() => setPendingStatus(null)}
        title={pendingStatus === FundStatus.Closed
          ? t('fundsManagement.closeFundNoteTitle')
          : t('fundsManagement.reopenFundNoteTitle')}
        message={t('fundsManagement.statusChangeNoteMessage', { id: fundId })}
        confirmText={t('fundsManagement.confirm')}
        isLoading={updateStatus.isPending}
        onConfirm={(note) => {
          if (pendingStatus) updateStatus.mutate({ params: {}, body: { status: pendingStatus, note } });
        }}
      />
    </Blade>
  );
};

export default FundBlade;
